import { useCallback, useEffect, useRef, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'
import { useRouter } from '@tanstack/react-router'
import type { LedgerServerQuery } from '@/components/ui/LedgerTable'
import {
  customerSearchSortKeys,
  type CustomerSearchSortKey,
} from '@/db/domain/customerSearch'
import { useDb } from '@/lib/dbContext'
import {
  CustomersScreen,
  type CustomerRow,
} from '@/screens/CustomersScreen'
import type { NewCustomerInitialValues } from '@/screens/NewCustomerScreen'

function isSortKey(key: string): key is CustomerSearchSortKey {
  return (customerSearchSortKeys as readonly string[]).includes(key)
}

/** Column keys whose text inputs should debounce before hitting the server. */
const debouncedFilterKeys = ['ref', 'customer', 'contact', 'address', 'invoiceTo']

function textFiltersChanged(
  a: Record<string, string>,
  b: Record<string, string>,
) {
  return debouncedFilterKeys.some((key) => (a[key] ?? '') !== (b[key] ?? ''))
}

export function CustomersScreenContainer() {
  const db = useDb()
  const router = useRouter()

  // The server search owns membership + order (which ids, what sequence,
  // what total); the records themselves live in the TanStack DB collection,
  // hydrated by the search, so every reader shares the same live data.
  const [membership, setMembership] = useState<{
    keys: string[]
    totalItems: number
  }>({ keys: [], totalItems: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const liveCustomers = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )

  const lastQueryRef = useRef<LedgerServerQuery | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeqRef = useRef(0)

  const runQuery = useCallback(
    async (query: LedgerServerQuery) => {
      const seq = ++requestSeqRef.current
      setLoading(true)
      try {
        const response = await db.searchCustomers({
          filters: query.filters,
          sort:
            query.sort && isSortKey(query.sort.key)
              ? { key: query.sort.key, dir: query.sort.dir }
              : undefined,
          page: query.page,
          pageSize: query.pageSize,
        })
        if (seq !== requestSeqRef.current) return
        setMembership({ keys: response.keys, totalItems: response.totalItems })
        setError(false)
      } catch (err) {
        if (seq !== requestSeqRef.current) return
        console.error('Customers search failed', err)
        setError(true)
      } finally {
        if (seq === requestSeqRef.current) setLoading(false)
      }
    },
    [db],
  )

  const handleQueryChange = useCallback(
    (query: LedgerServerQuery) => {
      const previous = lastQueryRef.current
      lastQueryRef.current = query
      if (debounceRef.current) clearTimeout(debounceRef.current)

      // Typing in a text filter debounces; sort, page and select changes fire
      // immediately.
      if (previous && textFiltersChanged(previous.filters, query.filters)) {
        debounceRef.current = setTimeout(() => runQuery(query), 300)
      } else {
        runQuery(query)
      }
    },
    [runQuery],
  )

  /** Re-runs the last search so mutations show up in the ledger. */
  const refresh = useCallback(() => {
    if (lastQueryRef.current) runQuery(lastQueryRef.current)
  }, [runQuery])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const customersById = new Map(
    (liveCustomers.data ?? []).map((customer) => [customer.id, customer]),
  )
  const rows: CustomerRow[] = membership.keys
    // A key can transiently miss its record (e.g. an optimistic delete)
    // until the next re-query settles membership.
    .flatMap((key) => {
      const customer = customersById.get(key)
      return customer ? [customer] : []
    })
    .map((customer) => ({
      id: customer.publicId ?? customer.id,
      privateId: customer.id,
      fullName: customer.fullName,
      externalId: customer.externalId,
      contactEmail: customer.contactEmail,
      contactPhone: customer.contactPhone,
      contactAddress: customer.contactAddress,
      invoiceName: customer.invoiceName,
      invoiceAddress: customer.invoiceAddress,
      invoiceEmail: customer.invoiceEmail,
      invoicePhone: customer.invoicePhone,
      invoiceVat: customer.invoiceVat,
      externalCustomerId: customer.externalCustomerId,
      createdAt: customer.createdAt,
      updatedAt: customer.updatedAt,
    }))

  const handleCreateCustomer = async (
    values: NewCustomerInitialValues,
  ) => {
    await db.createCustomerAndWait({
      fullName: values.fullName,
      externalId: values.externalId ?? '',
      contactAddress: values.contactAddress ?? '',
      contactEmail: values.contactEmail ?? '',
      contactPhone: values.contactPhone ?? '',
      invoiceName: values.invoiceName ?? values.fullName,
      invoiceAddress:
        values.invoiceAddress ??
        values.contactAddress ??
        '',
      invoiceEmail:
        values.invoiceEmail ??
        values.contactEmail ??
        '',
      invoicePhone:
        values.invoicePhone ??
        values.contactPhone ??
        '',
      invoiceVat: values.invoiceVat ?? '',
      externalCustomerId: values.externalCustomerId,
    })
    refresh()
  }

  const handleUpdateCustomer = async (
    id: string,
    values: NewCustomerInitialValues,
  ) => {
    await db.updateCustomerAndWait(id, {
      fullName: values.fullName,
      externalId: values.externalId ?? '',
      contactAddress: values.contactAddress ?? '',
      contactEmail: values.contactEmail ?? '',
      contactPhone: values.contactPhone ?? '',
      invoiceName: values.invoiceName ?? values.fullName,
      invoiceAddress:
        values.invoiceAddress ??
        values.contactAddress ??
        '',
      invoiceEmail:
        values.invoiceEmail ??
        values.contactEmail ??
        '',
      invoicePhone:
        values.invoicePhone ??
        values.contactPhone ??
        '',
      invoiceVat: values.invoiceVat ?? '',
      externalCustomerId: values.externalCustomerId,
    })
    refresh()
  }

  const handleDeleteCustomer = async (id: string) => {
    await db.deleteCustomerAndWait(id)
    refresh()
  }

  return (
    <CustomersScreen
      customers={rows}
      totalItems={membership.totalItems}
      loading={loading}
      error={error}
      onQueryChange={handleQueryChange}
      onCreateCustomer={handleCreateCustomer}
      onUpdateCustomer={handleUpdateCustomer}
      onDeleteCustomer={handleDeleteCustomer}
      onViewCustomer={(id) => {
        router.navigate({
          to: '/customers/$id',
          params: { id },
        })
      }}
    />
  )
}
